/**
 * NeoConfigModal: multi-select "Configure Instance" dialog.
 * Optional `searchable` adds a filter field and a max-height scrolling checkbox list.
 */
import React, { useMemo, useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Checkbox,
  FormControlLabel,
  Button,
  IconButton,
  CircularProgress,
  Link,
  TextField,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import successImage from "assets/img/NeoConfigSuccess.png";
import failureImage from "assets/img/NeoConfigFail.png";

export interface ConfigSelectionItem {
  name: string;
  groupId: string;
  description?: string;
  type?: "ACTION" | "SCRIPT";
  operatingSystems?: string[];
}

const operatingSystemLabels: Record<string, string> = { WINDOWS: "Windows", MAC: "macOS", LINUX: "Linux" };

interface NeoConfigModalProps {
  open: boolean;
  items: ConfigSelectionItem[];
  selectedItemIds: string[];
  loading: boolean;
  error: string | null;
  success: boolean | null;
  onSelect: (itemIds: string[]) => void;
  onClose: () => void;
  onConnect: () => Promise<void>;
  appName: string;
  itemLabelPlural?: string;
  /** Bold heading for the checkbox list section (overrides default queue-style prompt). */
  selectionHeading?: string;
  /** Short helper text under the selection heading. */
  selectionDescription?: string;
  /** Dialog width — use md for long script names. */
  dialogMaxWidth?: "xs" | "sm" | "md" | "lg";
  /** Optional content rendered above the multi-select list (e.g. Datto remote session picker). */
  topContent?: React.ReactNode;
  /** Divider between topContent and the checkbox list. */
  topContentDivider?: boolean;
  /** Show a local search box for long selection lists. */
  searchable?: boolean;
  searchPlaceholder?: string;
}

const NeoConfigModal: React.FC<NeoConfigModalProps> = ({
  open,
  items,
  selectedItemIds,
  loading,
  error,
  success,
  onSelect,
  onClose,
  onConnect,
  appName,
  itemLabelPlural = "queues",
  selectionHeading,
  selectionDescription,
  dialogMaxWidth = "sm",
  topContent,
  topContentDivider = false,
  searchable = false,
  searchPlaceholder,
}) => {
  const [localSelected, setLocalSelected] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  const [automationType, setAutomationType] = useState<"all" | "ACTION" | "SCRIPT">("all");
  const hasAutomationTypes = items.some((item) => item.type);
  const itemLabelPluralLower = itemLabelPlural.toLowerCase();

  useEffect(() => {
    setLocalSelected(selectedItemIds);
  }, [selectedItemIds, items]);

  useEffect(() => {
    if (!open) {
      setSearch("");
      setAutomationType("all");
    }
  }, [open]);

  const normalizedSearch = search.trim().toLowerCase();
  const visibleItems = useMemo(() => {
    return items.filter((item) =>
      (automationType === "all" || item.type === automationType) &&
      (!searchable || !normalizedSearch || [item.name, item.groupId, item.description, item.type === "ACTION" ? "Native action" : item.type, ...(item.operatingSystems || [])]
        .join(" ")
        .toLowerCase()
        .includes(normalizedSearch))
    );
  }, [items, normalizedSearch, searchable, automationType]);

  const selectTargetItems = visibleItems;
  const allTargetSelected =
    selectTargetItems.length > 0 &&
    selectTargetItems.every((item) => localSelected.includes(item.groupId));

  const handleToggleItem = (groupId: string) => {
    setLocalSelected((prev) => {
      const updated = prev.includes(groupId)
        ? prev.filter((q) => q !== groupId)
        : [...prev, groupId];
      onSelect(updated);
      return updated;
    });
  };

  const handleSelectAll = () => {
    if (allTargetSelected) {
      const targetIds = new Set(selectTargetItems.map((item) => item.groupId));
      const updated = localSelected.filter((itemId) => !targetIds.has(itemId));
      setLocalSelected(updated);
      onSelect(updated);
    } else {
      const updated = Array.from(new Set([...localSelected, ...selectTargetItems.map((item) => item.groupId)]));
      setLocalSelected(updated);
      onSelect(updated);
    }
  };

  // Modal content for different states
  let content;
  if (success === true) {
    content = (
      <Box textAlign="center" py={1}>
        <Box mb={3}>
          <img src={successImage} alt="Success" style={{ width: 327, height: 307, borderRadius: 3 }} />
        </Box>
        <Typography variant="h5" fontWeight={700} mb={2}>
          Configuration Successfully Completed
        </Typography>
      </Box>
    );
  } else if (success === false) {
    content = (
      <Box textAlign="center" py={1}>
        <Box mb={3}>
          <img src={failureImage} alt="Failure" style={{ width: 327, height: 307, borderRadius: 3 }} />
        </Box>
        <Typography variant="h5" fontWeight={700} mb={2}>
          Opps! Configuration Failed
        </Typography>
      </Box>
    );
  } else {
    const selectAllButton = (
      <Button
        size="small"
        sx={{ textTransform: "none", color: "#9B7AEF", fontWeight: 700 }}
        onClick={handleSelectAll}
        disabled={loading || selectTargetItems.length === 0}
      >
        {searchable
          ? allTargetSelected
            ? "Deselect Visible"
            : "Select Visible"
          : allTargetSelected
            ? "Deselect All"
            : "Select All"}
      </Button>
    );

    const selectionList = (
      <>
        {items.length === 0 ? (
          <Typography color="textSecondary">{`No ${itemLabelPluralLower} available.`}</Typography>
        ) : visibleItems.length === 0 ? (
          <Typography color="textSecondary">{`No ${itemLabelPluralLower} found.`}</Typography>
        ) : (
          visibleItems.map((item) => (
            <FormControlLabel
              key={item.groupId}
              control={
                <Checkbox
                  checked={localSelected.includes(item.groupId)}
                  onChange={() => handleToggleItem(item.groupId)}
                  disabled={loading}
                  sx={{
                    color: "#C7C7C7",
                    '&.Mui-checked': {
                      color: "#9B7AEF",
                    },
                  }}
                />
              }
              label={
                <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
                  <Box component="span" sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 14, fontWeight: 500, color: "#000" }}>
                    {item.name}
                  </Box>
                  {item.type ? (
                    <Box
                      component="span"
                      sx={{
                        px: 0.75,
                        py: 0.25,
                        borderRadius: "4px",
                        bgcolor: "#EEF2FF",
                        color: "#4F46E5",
                        fontFamily: "var(--theo-font-sans)",
                        fontSize: 11,
                        fontWeight: 600,
                      }}
                    >
                      {item.type === "ACTION" ? "Native action" : "Script"}
                    </Box>
                  ) : null}
                  {item.operatingSystems?.length ? (
                    <Typography component="span" sx={{ color: "#666F8D", fontSize: 12 }}>
                      {item.operatingSystems.map((os) => operatingSystemLabels[os] || os).join(", ")}
                    </Typography>
                  ) : null}
                </Box>
              }
              sx={{
                display: "block",
                mb: 1,
              }}
            />
          ))
        )}
      </>
    );

    const topBlock = topContent ? (
      <Box sx={{ mb: topContentDivider ? 0 : 3 }}>
        {topContent}
        {topContentDivider ? (
          <Box sx={{ borderBottom: "1px solid #E0E0E0", mt: 3, mb: 3 }} />
        ) : null}
      </Box>
    ) : null;

    const headingBlock = selectionHeading ? (
      <Box
        sx={{
          mb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 2,
        }}
      >
        <Box>
          <Typography
            sx={{ fontWeight: 700, fontSize: 14, fontFamily: "var(--theo-font-sans)", color: "#000" }}
          >
            {selectionHeading}
          </Typography>
          {selectionDescription ? (
            <Typography sx={{ fontSize: 13, color: "#666F8D", mt: 0.5, fontFamily: "var(--theo-font-sans)" }}>
              {selectionDescription}
            </Typography>
          ) : null}
        </Box>
        {selectAllButton}
      </Box>
    ) : (
      <Typography sx={{ mb: 2, fontWeight: 500, fontSize: 14, fontFamily: "var(--theo-font-sans)", color: "#000" }}>
        {`Select ${itemLabelPluralLower} to show in Theo (leave empty to show no ${itemLabelPluralLower})`}
        <Box component="span" sx={{ float: "right" }}>
          {selectAllButton}
        </Box>
      </Typography>
    );

    content = (
      <>
        {topBlock}
        {headingBlock}
        {hasAutomationTypes ? (
          <FormControl size="small" sx={{ minWidth: 180, mb: 2 }}>
            <InputLabel id="automation-type-label">Automation type</InputLabel>
            <Select
              labelId="automation-type-label"
              label="Automation type"
              value={automationType}
              onChange={(event) => setAutomationType(event.target.value as "all" | "ACTION" | "SCRIPT")}
            >
              <MenuItem value="all">All types</MenuItem>
              <MenuItem value="ACTION">Native actions</MenuItem>
              <MenuItem value="SCRIPT">Scripts</MenuItem>
            </Select>
          </FormControl>
        ) : null}
        {searchable ? (
          <TextField
            fullWidth
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={searchPlaceholder || `Search ${itemLabelPluralLower}`}
            size="small"
            InputProps={{ startAdornment: <SearchIcon sx={{ color: "#8A8FA3", mr: 1 }} /> }}
            sx={{
              mb: 2,
              "& .MuiInputBase-input": { fontFamily: "var(--theo-font-sans)", fontSize: 14 },
            }}
          />
        ) : null}
        <Box
          sx={
            searchable
              ? {
                  maxHeight: 280,
                  minHeight: 200,
                  overflowY: "auto",
                  border: "1px solid #E5E7EB",
                  borderRadius: "6px",
                  px: 1,
                  py: 1,
                  mb: 1,
                }
              : { mb: 3, minHeight: 310 }
          }
        >
          {selectionList}
        </Box>
      </>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={dialogMaxWidth}
      fullWidth
      PaperProps={{ sx: { borderRadius: "25px", padding: "8px" } }}
    >
      <DialogTitle sx={{ fontSize: 30, fontWeight: 500, fontFamily: "Inter", color: "#19213D", pb: 1 }}>
        {`Configure ${appName} Instance`}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: "absolute", right: 16, top: 16, color: "#705D4A" }}
        >
          <CloseIcon sx={{ fontSize: 36 }} />
        </IconButton>
      </DialogTitle>
      <Box sx={{ borderBottom: "1px solid #E0E0E0", mx: 3 }} />
      <DialogContent sx={{ pt: 3, pb: 1 }}>{content}</DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, justifyContent: "space-between" }}>
        <Link href="#" underline="none" sx={{ color: "#9B7AEF", fontWeight: 500, fontSize: 16, ml: 1 }}>
          Need help?
        </Link>
        {success === null && (
          <Box>
            <Button
              onClick={onClose}
              variant="contained"
              sx={{
                background: "#705D4A",
                color: "#fff",
                borderRadius: 2,
                minWidth: 70,
                mr: 1,
                textTransform: "none",
                fontWeight: 700,
                '&:hover': { background: "#5e4d3c" },
              }}
              disabled={loading}
            >
              Back
            </Button>
            <Button
              onClick={onConnect}
              variant="contained"
              sx={{
                background: "#F5F5F5",
                color: "#232B44",
                borderRadius: 2,
                minWidth: 100,
                textTransform: "none",
                fontWeight: 700,
                boxShadow: "none",
                '&:hover': { background: "#F5F5F5" },
              }}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {loading ? "Saving..." : "Save"}
            </Button>
          </Box>
        )}
        {success === true && (
          <Button
            onClick={onClose}
            variant="contained"
            sx={{
              background: "#F5F5F5",
              color: "#232B44",
              borderRadius: 2,
              minWidth: 140,
              textTransform: "none",
              fontWeight: 700,
              boxShadow: "none",
              '&:hover': { background: "#F5F5F5" },
            }}
          >
            Back To Home
          </Button>
        )}
        {success === false && (
          <Button
            onClick={onConnect}
            variant="contained"
            sx={{
              background: "#F5F5F5",
              color: "#232B44",
              borderRadius: 2,
              minWidth: 100,
              textTransform: "none",
              fontWeight: 700,
              boxShadow: "none",
              '&:hover': { background: "#F5F5F5" },
            }}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {loading ? "Saving..." : "Try Again"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default NeoConfigModal;
